import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { usePatchUpdateResource } from '@app/src/api/updateHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import EnrichedFieldIndicator from '@app/src/components/Form/EnrichedFieldIndicator'
import type { Option } from '@app/src/components/Form/Select'
import { OptionIcon } from '@app/src/components/Form/Select'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { FinalRiskRatingIcon } from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import TextField from '@app/src/components/Ui/TextField'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useOwnerRemovalConfirmation } from '@app/src/hooks/useOwnerRemovalConfirmation'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import RequestAutomationWarningAlert from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Warning/RequestAutomationWarningAlert'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Category } from '@app/src/types/categories'
import { StandardCategoryOptions } from '@app/src/types/categories'
import { SortOrder } from '@app/src/types/filter'
import type { GenericOrganization, NaceCode, Provider } from '@app/src/types/organizations'
import { CompanyRanges } from '@app/src/types/organizations'
import type { Country, User } from '@app/src/types/resourceExplorer'
import { flatten, isValidVatOrRegistrationNumber, validURL } from '@app/src/utils/helpers'
import { NotificationSeverity, ORGANIZATION_NAME_REGEX } from '@app/src/wf-constants'
import { RestoreOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Stack } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useQueryClient } from '@tanstack/react-query'
import React, { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { getOrganizationDisplayName } from '../../Table/Cells/CompanyCell'
import { useDrawer } from '../DrawerContext'
import DrawerView from '../DrawerView'

type DrawerViewOrganizationFormProps = {
  provider: Provider
}

const ENRICHABLE_FIELDS = [
  'websiteAddress',
  'registrationNumber',
  'industryId',
  'subIndustryId',
  'revenueRange',
  'companySizeRange',
] as const

const parseBlockedDomains = (value: string | undefined): string[] =>
  (value ?? '')
    .split(/[\s,;]+/)
    .map(domain => domain.trim())
    .filter(Boolean)

const DrawerViewOrganizationForm: React.FC<DrawerViewOrganizationFormProps> = ({ provider }) => {
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { closeDrawer } = useDrawer()
  const queryClient = useQueryClient()
  const { formatMessage } = useIntl()
  const { renderWithPermission, hasPermission } = usePermissions()
  const { mutate, isPending: isLoading } = usePatchUpdateResource<Partial<GenericOrganization>>()
  const { confirmOwnerChange } = useOwnerRemovalConfirmation()
  const { providerBlockedDomains } = useFlags()

  const enrichedValues = useProviderEnrichedValues(provider)
  const isUnconnectedProvider = !provider.organizationId
  const hasEnrichedValues = enrichedValues.latestEnrichment !== undefined

  const defaultValues = useMemo(() => {
    const flattened = {
      ...flatten(provider),
      blockedDomains: provider.blockedDomains?.join(', ') ?? '',
    } as unknown as { [key: string]: string | number | Array<unknown> }
    if (!isUnconnectedProvider) return flattened

    return {
      ...flattened,
      websiteAddress: enrichedValues.websiteAddress.value ?? flattened.websiteAddress,
      vatNumber: enrichedValues.vatNumber.value ?? flattened.vatNumber,
      registrationNumber: enrichedValues.registrationNumber.value ?? flattened.registrationNumber,
      industryId: enrichedValues.industryId.value ?? flattened.industryId,
      subIndustryId: enrichedValues.subIndustryId.value ?? flattened.subIndustryId,
      revenueRange: enrichedValues.revenueRange.value ?? flattened.revenueRange,
      companySizeRange: enrichedValues.companySizeRange.value ?? flattened.companySizeRange,
    }
  }, [provider, enrichedValues, isUnconnectedProvider])

  const {
    handleSubmit,
    control,
    formState: { dirtyFields },
    setError,
    register,
    setValue,
    errors,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  })

  const watchedIndustryId = watch('industryId') as number | undefined
  const prevIndustryIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (prevIndustryIdRef.current !== undefined && prevIndustryIdRef.current !== watchedIndustryId) {
      setValue('subIndustryId', undefined, { shouldDirty: true })
    }
    prevIndustryIdRef.current = watchedIndustryId
  }, [watchedIndustryId, setValue])

  const isEnrichedField = useCallback(
    (field: (typeof ENRICHABLE_FIELDS)[number] | 'vatNumber') =>
      isUnconnectedProvider && enrichedValues[field].isEnriched && !dirtyFields[field as keyof typeof dirtyFields],
    [isUnconnectedProvider, enrichedValues, dirtyFields],
  )

  const { data: categoriesWithOptions } = useFetchResourceWithPost<Category[]>({
    key: FetchKey.Category,
    endpoint: endpoints.categoriesWithIncludes,
    body: ['options'],
  })
  const hasCategoriesWithOptions = Array.isArray(categoriesWithOptions) && categoriesWithOptions.length > 0

  useEffect(() => {
    if (!hasCategoriesWithOptions) return
    const selectedIds = new Set(provider?.categoryOptions?.map(c => c.id))
    categoriesWithOptions.forEach(cat => {
      const preselectedIds = cat.options?.filter(o => selectedIds.has(o.id)).map(o => o.id) ?? []
      setValue(`categoryOptions.${cat.id}`, preselectedIds, { shouldDirty: false, shouldValidate: false })
    })
  }, [hasCategoriesWithOptions, categoriesWithOptions, provider.categoryOptions, setValue])

  useEffect(() => {
    if (!isUnconnectedProvider || !enrichedValues.latestEnrichment) return

    ENRICHABLE_FIELDS.forEach(field => {
      const enriched = enrichedValues[field]
      if (enriched.isEnriched && enriched.value) {
        setValue(field, enriched.value, { shouldDirty: false })
      }
    })
  }, [isUnconnectedProvider, enrichedValues, setValue])

  const restoreEnrichedValues = useCallback(() => {
    ENRICHABLE_FIELDS.forEach(field => {
      const enrichmentValue = enrichedValues[field].enrichmentValue
      if (enrichmentValue) {
        setValue(field, enrichmentValue, { shouldDirty: true })
      }
    })
  }, [enrichedValues, setValue])

  const onSubmit = async (values: Partial<GenericOrganization>): Promise<void> => {
    if (values.ownerUserIds !== undefined) {
      const confirmed = await confirmOwnerChange({
        originalOwnerIds: provider.ownerUserIds ?? [],
        newOwnerIds: values.ownerUserIds,
        providerId: provider.id,
        providerCount: 1,
      })
      if (!confirmed) return
    }

    const categoryOptionIds = Object.values(values.categoryOptions ?? {}).flatMap(ids =>
      Array.isArray(ids) ? ids : [],
    )

    const body: Partial<GenericOrganization> & { categoryOptionIds?: number[] } = {
      ...values,
      name: values.name?.trim(),
      categoryOptionIds,
    }
    delete body.categoryOptions
    if ('blockedDomains' in values) {
      body.blockedDomains = parseBlockedDomains(values.blockedDomains as unknown as string)
    }

    if (isUnconnectedProvider) {
      ENRICHABLE_FIELDS.forEach(field => {
        const wasNotTouched = !dirtyFields[field as keyof typeof dirtyFields]
        const matchesEnrichment = values[field as keyof typeof values] === enrichedValues[field].enrichmentValue
        if (wasNotTouched && matchesEnrichment) {
          delete body[field as keyof typeof body]
        }
      })
    }

    mutate(
      {
        body: body,
        url: endpoints.provider(provider.id),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries()
          showSnackbar({
            message: formatMessage({ id: 'notifications.successfulResourceSave' }),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })
          closeDrawer()
        },
        onError: error => {
          if (error.isValidationError) {
            error.setFormValidationErrors(setError)
            return
          }
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'general.categorize' })}
      subTitle={getOrganizationDisplayName(provider)}
      buttons={[
        {
          label: formatMessage({ id: 'reporting.submit' }),
          variant: 'contained',
          loading: isLoading,
          type: 'submit',
        },
      ]}
      onFormSubmit={handleSubmit(onSubmit)}
      disableScrolling
    >
      <Stack p={2} spacing={3} overflow="auto">
        <RequestAutomationWarningAlert
          automationType={RequestAutomationType.FOR_PROVIDERS_IN_CATEGORY}
          sx={{ borderRadius: 0 }}
          closeDrawer={closeDrawer}
        />
        {Object.keys(errors ?? {})?.length > 0 && (
          <Alert elevation={1} variant="filled" severity="error">
            {formatMessage({ id: 'form.validation.error' })}
          </Alert>
        )}
        {!provider.organizationId && (
          <>
            <TextField
              hoveringLabel
              inputRef={register({
                required: formatMessage({ id: 'form.validation.required' }),
                minLength: {
                  value: 2,
                  message: formatMessage({ id: 'errorMessages.general.minLength' }, { count: 2 }),
                },
                pattern: {
                  value: ORGANIZATION_NAME_REGEX,
                  message: formatMessage({ id: 'form.validation.latinCharactersOnly' }),
                },
              })}
              fullWidth
              required
              name="name"
              label={formatMessage({ id: 'schemas.provider.name' })}
              error={Boolean(errors?.name)}
              type="text"
              helperText={errors?.name?.message}
              onClear={(): void => setValue('name', '')}
            />
            <Select<number, Country>
              hoveringLabel
              name="countryId"
              rules={{ required: 'This field is required' }}
              fieldLabel={formatMessage({ id: 'schemas.provider.country' })}
              control={control}
              required
              forceFetch
              enableAutoSelect
              navigation={{
                url: endpoints.countries,
                type: 'post',
                postObject: {
                  include: [],
                  filter: [],
                  sort: { target: 'name', order: SortOrder.ASCENDING },
                },
              }}
              objectToOption={(country: Country): Option<number> => {
                return {
                  label: country.name,
                  value: country.id,
                }
              }}
              error={errors?.countryId?.message}
            />
            <Box>
              <TextField
                hoveringLabel
                placeholder={formatMessage({ id: 'textFieldPlaceholders.websiteExample' })}
                inputRef={register({
                  validate: value =>
                    !value?.trim() || validURL(value) || formatMessage({ id: 'notifications.errorInvalidWebsite' }),
                })}
                fullWidth
                name="websiteAddress"
                label={formatMessage({ id: 'schemas.organization.websiteAddress' })}
                error={Boolean(errors?.websiteAddress)}
                type="text"
                helperText={errors?.websiteAddress?.message}
              />
              <EnrichedFieldIndicator show={isEnrichedField('websiteAddress')} />
            </Box>
          </>
        )}
        <Box>
          <TextField
            hoveringLabel
            inputRef={register({
              validate: (value: string) =>
                value === '' ||
                isValidVatOrRegistrationNumber(value) ||
                formatMessage({ id: 'notifications.errorInvalidRegistrationNumber' }),
            })}
            fullWidth
            name="registrationNumber"
            label={formatMessage({ id: 'schemas.organization.registrationNumber' })}
            error={Boolean(errors?.registrationNumber)}
            type="text"
            helperText={errors?.registrationNumber?.message}
            onClear={(): void => setValue('registrationNumber', '')}
          />
          <EnrichedFieldIndicator show={isEnrichedField('registrationNumber')} />
        </Box>
        {providerBlockedDomains && hasPermission(Permissions.ACCESS_SOLUTION_CONFIGURATION) && (
          <TextField
            hoveringLabel
            placeholder={formatMessage({ id: 'textFieldPlaceholders.blockedDomainsExample' })}
            inputRef={register}
            fullWidth
            name="blockedDomains"
            label={formatMessage({ id: 'schemas.organization.blockedDomains' })}
            type="text"
            helperText={formatMessage({ id: 'schemas.organization.blockedDomainsHelper' })}
            onClear={(): void => setValue('blockedDomains', '')}
          />
        )}
        {renderWithPermission(
          {
            [Permissions.SOURCING_USER]: (
              <Fragment key="sourcing">
                <TextField
                  hoveringLabel
                  inputRef={register}
                  fullWidth
                  name="duns"
                  label={formatMessage({ id: 'schemas.supplier.duns' })}
                  error={Boolean(errors?.duns)}
                  type="text"
                  helperText={errors?.duns?.message}
                  onClear={(): void => setValue('duns', '')}
                />
              </Fragment>
            ),
          },
          { multiple: true },
        )}
        <Box>
          <TextField
            hoveringLabel
            inputRef={register({
              validate: (value: string) =>
                value === '' ||
                isValidVatOrRegistrationNumber(value) ||
                formatMessage({ id: 'notifications.errorInvalidVatNumber' }),
            })}
            fullWidth
            name="vatNumber"
            label={formatMessage({ id: 'schemas.organization.vatNumber' })}
            error={Boolean(errors?.vatNumber)}
            type="text"
            helperText={errors?.vatNumber?.message}
            onClear={(): void => setValue('vatNumber', '')}
          />
          <EnrichedFieldIndicator show={isEnrichedField('vatNumber')} />
        </Box>
        {isUnconnectedProvider && (
          <>
            <Box>
              <Select<number, NaceCode>
                name="industryId"
                control={control}
                hoveringLabel
                forceFetch
                defaultValue={
                  (enrichedValues.industryId.value as number) || (provider.industryId as number) || undefined
                }
                error={errors?.industryId?.message}
                objectToOption={object => ({
                  label: object.description,
                  value: object.id,
                })}
                findSelectedValue={(value, option) => value === option.value}
                navigation={{
                  url: endpoints.naceCodes,
                  type: 'post',
                  postObject: {
                    filter: [
                      {
                        name: 'parentId',
                        filters: [{ operator: Operators.IsNull }],
                      },
                    ],
                    include: [],
                    sort: { target: 'description', order: SortOrder.ASCENDING },
                  },
                }}
                fieldLabel={formatMessage({ id: 'schemas.organization.industry' })}
              />
              <EnrichedFieldIndicator show={isEnrichedField('industryId')} />
            </Box>
            <Box>
              <Select<number, NaceCode>
                name="subIndustryId"
                control={control}
                hoveringLabel
                forceFetch={Boolean(watchedIndustryId)}
                defaultValue={
                  (enrichedValues.subIndustryId.value as number) || (provider.subIndustryId as number) || undefined
                }
                error={errors?.subIndustryId?.message}
                disabled={!watchedIndustryId}
                objectToOption={object => ({
                  label: object.description,
                  value: object.id,
                })}
                findSelectedValue={(value, option) => value === option.value}
                navigation={{
                  url: endpoints.naceCodes,
                  type: 'post',
                  postObject: {
                    filter: [
                      {
                        name: 'parentId',
                        filters: [{ operator: Operators.EqualTo, value: watchedIndustryId as number }],
                      },
                    ],
                    include: [],
                    sort: { target: 'description', order: SortOrder.ASCENDING },
                  },
                }}
                fieldLabel={formatMessage({ id: 'schemas.organization.subIndustry' })}
              />
              <EnrichedFieldIndicator show={isEnrichedField('subIndustryId')} />
            </Box>
            <Box>
              <Select
                fieldLabel={formatMessage({ id: 'schemas.organization.revenue' })}
                hoveringLabel
                name="revenueRange"
                control={control}
                error={errors?.revenueRange?.message}
                options={Object.values(CompanyRanges).map(range => ({
                  label: formatMessage({ id: `claim.companyRevenue.${range}` }),
                  value: range,
                }))}
              />
              <EnrichedFieldIndicator show={isEnrichedField('revenueRange')} />
            </Box>
            <Box>
              <Select
                fieldLabel={formatMessage({ id: 'schemas.organization.employees' })}
                name="companySizeRange"
                hoveringLabel
                control={control}
                error={errors?.companySizeRange?.message}
                options={Object.values(CompanyRanges).map(range => ({
                  label: formatMessage({ id: `claim.companySize.${range}` }),
                  value: range,
                }))}
              />
              <EnrichedFieldIndicator show={isEnrichedField('companySizeRange')} />
            </Box>
          </>
        )}
        <TextField
          hoveringLabel
          inputRef={register}
          fullWidth
          name="customId"
          label={formatMessage({ id: 'schemas.organization.customId' })}
          error={Boolean(errors?.customId)}
          type="text"
          helperText={errors?.customId?.message}
          onClear={(): void => setValue('customId', '')}
        />
        {!hasPermission(Permissions.FINANCE_USER) && (
          <>
            <Select
              hoveringLabel
              name="activityStatus"
              fieldLabel={formatMessage({ id: 'schemas.provider.activityStatus' })}
              control={control}
              enableAutoSelect
              options={StandardCategoryOptions.activityStatus.map(option => ({
                value: option,
                label: formatMessage({ id: `schemas.provider.activityStatusValues.${option}` }),
              }))}
              error={errors?.activityStatus}
            />
          </>
        )}
        <Select
          hoveringLabel
          name="finalRiskRating"
          fieldLabel={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
          control={control}
          enableAutoSelect
          options={StandardCategoryOptions.finalRiskRating.map(option => ({
            value: option,
            label: formatMessage({ id: `schemas.provider.finalRiskRatingValues.${option}` }),
          }))}
          error={errors?.finalRiskRating}
          renderOption={(props, option, { selected }): JSX.Element => (
            <li {...props}>
              <OptionIcon selected={selected} multiple />
              <FinalRiskRatingIcon value={option?.value} />
              {option?.label !== undefined
                ? formatMessage({ id: `schemas.provider.finalRiskRatingValues.${option.value}` })
                : ''}
            </li>
          )}
        />
        <Select
          hoveringLabel
          name="priority"
          fieldLabel={formatMessage({ id: 'schemas.provider.priority' })}
          control={control}
          enableAutoSelect
          options={StandardCategoryOptions.priority.map(option => ({
            value: option,
            label: formatMessage({ id: `schemas.provider.priorityValues.${option}` }),
          }))}
          error={errors?.priority}
        />
        {!hasPermission(Permissions.FINANCE_USER) && (
          <Select
            hoveringLabel
            name="providerApprovalStatus"
            fieldLabel={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
            control={control}
            enableAutoSelect
            options={StandardCategoryOptions.providerApprovalStatus.map(option => ({
              value: option,
              label: formatMessage({ id: `schemas.provider.providerApprovalStatusValues.${option}` }),
            }))}
            error={errors?.providerApprovalStatus}
          />
        )}
        <Select<number, User>
          hoveringLabel
          multiple
          name="ownerUserIds"
          fieldLabel={formatMessage({ id: 'schemas.provider.ownerUsers' })}
          control={control}
          forceFetch
          enableAutoSelect
          navigation={{
            url: endpoints.colleagues,
            type: 'post',
            postObject: {
              pagination: {
                itemsPerPage: 9999,
                pageNumber: 1,
              },
              include: [],
              filter: [],
            },
          }}
          objectToOption={(owner: User): Option<number> => ({
            label: owner.name,
            value: owner.id,
          })}
          error={errors?.ownerUserIds}
        />

        {renderWithPermission(
          {
            [Permissions.SOURCING_USER]: (
              <Fragment key="sourcing">
                <Select
                  hoveringLabel
                  name="tier"
                  fieldLabel={formatMessage({ id: 'schemas.supplier.tier' })}
                  control={control}
                  enableAutoSelect
                  options={Array.from(Array(9).keys()).map(i => ({
                    value: i,
                    label: formatMessage({ id: 'schemas.provider.tierValues' }, { tier: i }),
                  }))}
                  error={errors?.tier}
                />
                <Select
                  hoveringLabel
                  name="supplierUsage"
                  fieldLabel={formatMessage({ id: 'schemas.supplier.supplierUsage' })}
                  control={control}
                  enableAutoSelect
                  options={[
                    { value: 'Direct', label: 'Direct' },
                    { value: 'Indirect', label: 'Indirect' },
                  ]}
                  error={errors?.supplierUsage}
                />
              </Fragment>
            ),
          },
          { multiple: true },
        )}
        {hasCategoriesWithOptions &&
          categoriesWithOptions.map(category => (
            <Select
              key={category.id}
              hoveringLabel
              name={`categoryOptions.${category.id}`}
              fieldLabel={category.name}
              control={control}
              multiple
              enableAutoSelect
              options={(category.options ?? []).map(opt => ({
                value: opt.id,
                label: opt.name,
              }))}
              error={errors?.categories}
            />
          ))}
        {isUnconnectedProvider && hasEnrichedValues && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="text"
              startIcon={<RestoreOutlined />}
              onClick={restoreEnrichedValues}
              sx={{ color: 'text.secondary' }}
            >
              {formatMessage({ id: 'general.restoreEnrichedProperties' })}
            </Button>
          </Box>
        )}
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewOrganizationForm
